import cx from 'clsx';
import { addMinutes, format } from 'date-fns';
import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import MockupImg from '@assets/images/mockup-img.png';
import { ReactComponent as CopyIcon } from '@assets/svg/ic-copy.svg';

import ButtonCopyDetails from '@components/common/ButtonCopyDetails/ButtonCopyDetails';
import Gallery from '@components/common/Gallery/Gallery';
import InfoField from '@components/common/InfoField/InfoField';
import Button from '@components/uikit/Button/Button';
import Label, { type TVariant as LabelVariant } from '@components/uikit/Label/Label';
import StatusLabel, { type TVariant as StatusLabelVariant } from '@components/uikit/StatusLabel/StatusLabel';

import { MODAL_NAMES } from '@constants/modalRenderers';

import { calcSensorHex } from '@helpers/helper';
import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import type { IActionItem } from '@model/actionItem';

import useLiveTranslateNetworkTextBlocks from '@pages/ActionItem/hooks/useLiveTranslateNetworkTextBlocks';
import { selectAITranslateNetworkAiTextBlocks } from '@pages/ActionItem/store/useAITranslateStore';

import { useActionItemStaticDataQuery } from '@services/actionItem/useActionItemStaticDataQuery';
import { useActionItemMetaInfoQuery } from '@services/useActionItemMetaInfoQuery';
import { useInstallationPointPhotosQuery } from '@services/useInstallationPointPhotosQuery';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { useModalActions } from '@store/useModalStore';

import { getEquipmentHexSerial } from '../../helpers/getEquipmentHexSerial';
import { getEquipmentType } from '../../helpers/getEquipmentType';
import { getInstPointInfo } from '../../helpers/getInstPointInfo';
import useLiveTranslate from '../../hooks/useLiveTranslate';
import type { IAiControls } from '../../model/IAiControls';
import Device from '../Device/Device';
import EditField from '../EditField/EditField';
import TranslateBtn from '../TranslateBtn/TranslateBtn';
import TechnicalInfoAISkeleton from './TechnicalInfoAISkeleton';
import style from './style.module.scss';

interface IProps {
  actionItem?: IActionItem;
  controls: IAiControls;
}

interface IUrgencyState {
  tooltipContent: string;
  labelVariant: LabelVariant;
  statusLabelVariant: StatusLabelVariant;
}

const TechnicalInfoAI: React.FC<IProps> = ({ actionItem, controls }) => {
  const { t } = useTranslation();
  const { customerId, actionItemId } = useParams();
  const { pathname } = useLocation();

  const { setActiveModal } = useModalActions();
  const translatesNetworkTextBlocks = selectAITranslateNetworkAiTextBlocks();

  const { data: profileData, isFetching: profileIsFetching } = useUserProfileQuery();
  const { data: aiMetaInfoData } = useActionItemMetaInfoQuery();

  const instPointId = actionItem?.installation_point?.id;

  const { data: imagesData, isFetching: imagesIsFetching } = useInstallationPointPhotosQuery({
    installationPointId: actionItem?.installation_point?.id,
    enabled: !!instPointId && +instPointId !== -1,
  });

  const { data: aiStaticData, isFetching: aiStaticDataIsFetching } = useActionItemStaticDataQuery({
    actionItemId,
    installationPointId: instPointId,
  });

  const { hasTranslate, translates, isTranslated, isTranslateLoading, toggleIsTranslate } = useLiveTranslate({
    item: actionItem,
    fields: ['title'],
    entityId: actionItemId,
  });

  const { isTranslateLoading: isTranslateNetworkBlocks, toggleIsTranslate: toggleNetworkTextBlocks } =
    useLiveTranslateNetworkTextBlocks({
      item: actionItem,
      fields: ['description'],
      entityId: actionItem?.id,
      translationFields: ['description', 'disclaimer', 'estimated_time', 'tools'],
    });

  const isLoading =
    !actionItem ||
    profileIsFetching ||
    imagesIsFetching ||
    aiStaticDataIsFetching ||
    isTranslateNetworkBlocks;

  if (isLoading) return <TechnicalInfoAISkeleton />;

  const dateFormat = `${profileData?.dateFormat.date} ${profileData?.dateFormat.time}`;
  const point = actionItem?.installation_point;
  const equipment = point?.equipment;
  const equipmentType = actionItem?.equipment_type;

  const showWorkOrder =
    (!profileData?.customer.hide_wo || profileData?.customer.api_token) && !controls?.isInstPointTypeBoolean;
  const permissionChangeWO =
    !profileData?.customer.hide_wo && profileData?.userAccess.manageData && !controls?.isArchive;
  const hasNetworkExtraInfo = !!actionItem?.networkAiTextBlocks;

  const deviceId =
    point?.id || actionItem.router?.router_id || actionItem.gateway?.gateway_id || actionItem.node?.node_id;

  const typeName = getEquipmentType(actionItem.equipment_type, actionItem.installation_point?.point_type);

  const instPointInfo = getInstPointInfo(point, equipmentType, t);

  const equipmentHexSerial = getEquipmentHexSerial(typeName, point, actionItem, controls);

  const urgencyStates: Record<number, IUrgencyState> = {
    1: {
      tooltipContent:
        'There is an opportunity to extend equipment life with adjustments in lubrication, cleaning, routine maintenance, or operational practices.',
      labelVariant: 'standard',
      statusLabelVariant: 'standard',
    },
    2: {
      tooltipContent:
        'A moderate phase of wear has been reached. It is recommended to begin preparing for repairs by ordering parts and scheduling labor and downtime.',
      labelVariant: 'high',
      statusLabelVariant: 'high',
    },
    3: {
      tooltipContent: 'A machine failure could be imminent. Please investigate as soon as possible.',
      labelVariant: 'critical',
      statusLabelVariant: 'critical',
    },
  };

  const copyHexSerial = () => {
    try {
      navigator.clipboard.writeText(equipmentHexSerial).then(() => {
        showToast({ variant: 'success', message: t('Successfully copied to clipboard') });
      });
    } catch (e) {
      console.log(e);
    }
  };

  const aiImages = () => {
    if (controls.isOnboardingMockupAi) {
      return [{ url: MockupImg, noResize: true }];
    }

    return imagesData?.length ? imagesData : aiStaticData?.photos;
  };

  const disclaimer =
    isTranslated && translatesNetworkTextBlocks.disclaimer
      ? translatesNetworkTextBlocks.disclaimer
      : actionItem.networkAiTextBlocks?.disclaimer || '';
  const estimated_time =
    isTranslated && translatesNetworkTextBlocks.estimated_time
      ? translatesNetworkTextBlocks.estimated_time
      : actionItem.networkAiTextBlocks?.estimated_time || '';
  const tools =
    isTranslated && translatesNetworkTextBlocks.tools
      ? translatesNetworkTextBlocks.tools
      : actionItem.networkAiTextBlocks?.tools || '';

  const handleToggleIsTranslate = () => {
    toggleIsTranslate();
    if (actionItem.networkAiTextBlocks) {
      toggleNetworkTextBlocks();
    }
  };

  return (
    <div>
      <div className={style.blockHead}>
        <div className={style.blockTitle} id="tourStep-13">
          {t('Technical information')}
          <ButtonCopyDetails
            id="copyTechnicalInfo"
            copyDetailsData={actionItem?.technicalInfo}
            tooltipInfo={t('Copy Technical information.')}
          />
        </div>
        <TranslateBtn
          isTranslated={isTranslated}
          hasTranslate={hasTranslate}
          isTranslateLoading={isTranslateLoading}
          toggleIsTranslate={handleToggleIsTranslate}
        />
      </div>
      <div
        className={cx(style.blockContainer, {
          [style.simpleBlock]: !controls?.showReplaceSensor,
          [style.networkExtraBlock]: disclaimer,
        })}
      >
        <div className={style.actionItemHead}>
          <StatusLabel
            text={translates['title']}
            variant={urgencyStates[+actionItem?.urgency].statusLabelVariant}
          />
          <Label
            text={t(aiMetaInfoData?.actionListUrgencies?.byKey[+actionItem?.urgency])}
            tooltipContent={t(urgencyStates[+actionItem?.urgency].tooltipContent)}
            variant={urgencyStates[+actionItem?.urgency].labelVariant}
            tooltipId="ai-details-info-tooltip"
          />
          {disclaimer && <div className={style.disclaimer}>{disclaimer}</div>}
        </div>
        <div className={style.extraInformation}>
          <Device actionItem={actionItem} deviceId={deviceId} />
          <InfoField
            label={t(controls?.isSensor || controls?.isMote ? 'Hex Code' : 'Serial Number')}
            value={equipmentHexSerial}
            rightElement={
              equipmentHexSerial.length >= 6 ? (
                <CopyIcon
                  className={style.copyIcon}
                  onClick={() => copyHexSerial()}
                  data-tooltip-id="ai-details-info-tooltip"
                  data-tooltip-content={t('Copy hex code')}
                />
              ) : undefined
            }
          />
          <InfoField label={t('Asset Code')} value={equipment?.asset_code || '-'} />
          {actionItem?.old_sensor_id_hex && (
            <InfoField label={t('Old Sensor')} value={actionItem?.old_sensor_id_hex} />
          )}
          {showWorkOrder && hasNetworkExtraInfo && (
            <EditField
              woNumber={(actionItem.wo_number || '').toUpperCase()}
              actionItemId={actionItem.id}
              permissionChangeWO={permissionChangeWO}
            />
          )}
        </div>
        <div className={style.mainInformation}>
          {actionItem?.approximate_date && (
            <InfoField
              label={t('The approximate date and time of the failure')}
              value={format(
                addMinutes(actionItem?.approximate_date, actionItem?.facility?.timezone?.offset * 60),
                dateFormat,
              )}
            />
          )}
          {estimated_time && <InfoField label={t('Estimated time')} value={estimated_time} />}
          {tools && <InfoField label={t('Tools')} value={tools} />}
          <InfoField
            label={t('Installation point')}
            value={instPointInfo || '-'}
            rightElement={
              <Link
                to={`/customer/${customerId}/ai/${actionItemId}/history`}
                state={{ prevPage: pathname }}
                className={style.link}
                data-tooltip-id="ai-details-info-tooltip"
                data-tooltip-content={t(
                  'View the history of all Action Items on the installation point or Asset',
                )}
              >
                {t('Show history')}
              </Link>
            }
          />
          {typeName !== 'gateway' && typeName !== 'repeater' && typeName !== 'node' && (
            <InfoField
              label={t('Equipment')}
              value={actionItem?.equipmentName}
              rightElement={
                equipment?.id ? (
                  <Link
                    to={`/customer/${customerId}/sensors/${equipment?.id}/list`}
                    state={{ showNotesBlock: true, prevPage: pathname }}
                    className={style.link}
                    data-tooltip-id="ai-details-info-tooltip"
                    data-tooltip-content={t('Click here to go to the sensor list or view Asset notes.')}
                  >
                    {t('Asset details')}
                  </Link>
                ) : undefined
              }
            />
          )}
          <InfoField label={t('Location')} value={actionItem?.locationName} />
          <InfoField label={t('Facility name')} value={actionItem?.facility?.name || '-'} />
          {showWorkOrder && !hasNetworkExtraInfo && (
            <EditField
              woNumber={(actionItem.wo_number || '').toUpperCase()}
              actionItemId={actionItem.id}
              permissionChangeWO={permissionChangeWO}
            />
          )}
        </div>
        {controls?.showReplaceSensor && (
          <div className={style.infoActions}>
            <Button
              type="outline"
              variant={'small'}
              text={t('Replace Sensor')}
              onClick={() =>
                setActiveModal({
                  name: MODAL_NAMES.sensorReplaceModal,
                  options: { actionItem, instPoint: point },
                })
              }
            />
          </div>
        )}

        <div className={cx(style.galleryBlock, { [style.simpleGalleryBlock]: !controls?.showReplaceSensor })}>
          <Gallery images={aiImages()} />
        </div>
      </div>
      <Tooltip id="ai-details-info-tooltip" className={style.aiDetailsInfoTooltip} />
    </div>
  );
};

export default TechnicalInfoAI;
